import React, { forwardRef } from "react";
import logo from "../assets/logo.png";
import { getVehicleInfo } from "./billingVehicle";
import { BANK_DETAILS } from "./bankDetails";

// Right-aligned "Label : value" row used in the invoice meta column.
const MetaRow = ({ label, value }) => (
  <div className="flex justify-end gap-3">
    <span className="text-gray-600">{label} :</span>
    <span className="text-gray-900 w-32 text-right">{value}</span>
  </div>
);

// Full A4 invoice — the alternative to the 80mm thermal Receipt. Takes exactly
// the same props so the two are interchangeable wherever a sale needs to be
// documented (see BillingDocument).
//
// Everything here has to survive two very different renderers: window.print()
// and html2canvas (for the WhatsApp share image). That rules out CSS filters,
// object-fit and box-shadows, which html2canvas silently drops.
const Invoice = forwardRef(({ sale, cartItems }, ref) => {
  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-LK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const rawItems = sale ? sale.items : cartItems || [];

  // Same discount handling as Receipt: backend history stores `discount` as a
  // per-unit cash value, the live cart stores `discountPercent`.
  let grandTotal = 0;
  let totalSavings = 0;

  const items = rawItems.map((item) => {
    const originalPrice = parseFloat(item.unit_price || item.sell_price || 0);
    const qty = parseInt(item.quantity || 1);

    let discountAmount = 0;
    if (item.discount !== undefined && item.discount !== null) {
      discountAmount = parseFloat(item.discount);
    } else if (item.discountPercent !== undefined) {
      discountAmount = originalPrice * (parseFloat(item.discountPercent) / 100);
    }

    const finalPrice = originalPrice - discountAmount;
    const lineTotal = finalPrice * qty;

    grandTotal += lineTotal;
    totalSavings += discountAmount * qty;

    return {
      name: item.part_name || item.name || item.description || "Item",
      part_number: item.part_number,
      isLabor: item.item_type === "LABOR",
      qty,
      originalPrice,
      discountAmount,
      lineTotal,
    };
  });

  // Parts first, then the repairs done — the two are read for different
  // reasons (warranty/returns vs. workmanship), so the invoice groups them
  // instead of interleaving them in cart order. No headings: the part
  // numbers under the part names already mark where one ends and the other
  // begins. Numbering runs continuously so a line has one clear reference.
  const partItems = items.filter((i) => !i.isLabor);
  const laborItems = items.filter((i) => i.isLabor);

  // `offset` carries the running line number across the two groups.
  const renderRows = (list, offset) =>
    list.map((item, index) => (
      <tr key={`${offset}-${index}`} className="border-b border-gray-200 align-top">
        <td className="py-1.5 pl-3 pr-2 text-gray-600">{offset + index + 1}</td>
        <td className="py-1.5 px-2">
          <div className="font-bold text-gray-900">{item.name}</div>
          {item.part_number && (
            <div className="text-[9px] text-gray-500">{item.part_number}</div>
          )}
        </td>
        <td className="py-1.5 px-2 text-right">{item.qty.toFixed(2)}</td>
        <td className="py-1.5 px-2 text-right">{formatAmount(item.originalPrice)}</td>
        <td className="py-1.5 px-2 text-right">
          {formatAmount(item.discountAmount * item.qty)}
        </td>
        <td className="py-1.5 pl-2 pr-3 text-right">{formatAmount(item.lineTotal)}</td>
      </tr>
    ));

  const issuedAt = sale ? new Date(sale.created_at) : new Date();
  const issuedDate = issuedAt.toLocaleDateString("en-GB");
  const invoiceId = sale ? sale.id.substring(0, 8).toUpperCase() : "DRAFT";
  const customer = sale ? sale.customer_name : "Walk-in";
  // Plate, make/model and the last known odometer reading with its date.
  const vehicleInfo = getVehicleInfo(sale);

  const paymentStatus = sale ? sale.payment_status : "PAID";
  const amountPaid =
    paymentStatus === "PAID" ? grandTotal : parseFloat(sale?.amount_paid || 0);
  const balanceDue = Math.max(grandTotal - amountPaid, 0);

  const termsLabel =
    paymentStatus === "CREDIT"
      ? "Pay Later (Credit)"
      : paymentStatus === "PARTIAL"
      ? "Balance On Credit"
      : "Paid In Full";

  // Joined rather than inlined so the settlement sentence, which only applies
  // to an unpaid invoice, can drop out without leaving a double space behind.
  const footerNotes = [
    "Thanks for your business.",
    balanceDue > 0
      ? "Please settle the outstanding balance within 14 days, quoting the invoice number above."
      : "",
    "Please retain this invoice for warranty claims.",
    "No refunds or returns on electrical parts.",
    "Goods remain the property of NSS Auto Engineers until paid in full.",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={ref}
      className="billing-invoice bg-white text-black mx-auto text-[10px] leading-normal"
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "16mm 14mm",
        fontFamily: "Helvetica, Arial, sans-serif",
      }}
    >
      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-start">
        {/* Width-only sizing (never a fixed box): the mark is roughly 2.4:1 and
            html2canvas ignores object-fit when rasterizing this invoice for the
            WhatsApp share, so a fixed height would come out stretched even
            though the browser letterboxes it correctly.

            The artwork also carries empty canvas around the ink — it measures
            771x324 but the red mark only occupies x 102-642, y 57-293. That
            padding scales with the image, so at w-28 it renders as 15px left,
            8px top and 5px bottom, pushing the mark out of line with the
            address block below. These margins cancel it so the *ink* sits
            flush, and are tied to both this asset and the w-28 width: changing
            either one invalidates them. The right side is left alone since
            nothing aligns to it. */}
        <img
          src={logo}
          alt="NSS Auto Engineers"
          className="w-28 h-auto"
          style={{ marginLeft: "-15px", marginTop: "-8px", marginBottom: "-5px" }}
        />

        <div className="text-right">
          <div className="text-3xl font-bold tracking-tight leading-none">INVOICE</div>
          <div className="text-gray-500 mt-1.5">Invoice# {invoiceId}</div>
        </div>
      </div>

      {/* COMPANY + BALANCE DUE */}
      <div className="flex justify-between items-start mt-4">
        <div className="text-gray-800 leading-relaxed">
          <div className="font-bold text-gray-900">NSS Auto Engineers</div>
          <div>No. 272 Thudella</div>
          <div>Ja-ela, Sri Lanka</div>
          <div>+94 71 618 8187</div>
        </div>

        <div className="text-right">
          <div className="text-gray-500">Balance Due</div>
          <div className="text-xl font-bold mt-0.5">LKR {formatAmount(balanceDue)}</div>
        </div>
      </div>

      {/* ── BILL TO + META ──────────────────────────────────────────────── */}
      {/* This block is as tall as its taller column, so the vehicle facts live
          on the left under the customer rather than as four more labelled rows
          on the right — the left column would otherwise sit empty, and every
          line here is a line stolen from the items table on a long sale. */}
      <div className="flex justify-between items-start gap-6 mt-5">
        {/* Name only — the customer's phone number is deliberately kept off the
            printed bill; it's still on the sale record for the WhatsApp share. */}
        <div className="min-w-0">
          <div className="font-bold text-gray-900 mb-0.5">Bill To</div>
          <div className="font-bold text-gray-900">{customer || "Walk-in Customer"}</div>
          {vehicleInfo.vehicleLine && (
            <div className="text-gray-700 mt-0.5">{vehicleInfo.vehicleLine}</div>
          )}
          {vehicleInfo.mileageLine && (
            <div className="text-gray-700">{vehicleInfo.mileageLine}</div>
          )}
        </div>

        <div className="space-y-1 shrink-0">
          <MetaRow label="Invoice Date" value={issuedDate} />
          <MetaRow label="Terms" value={termsLabel} />
        </div>
      </div>

      {/* ── LINE ITEMS ──────────────────────────────────────────────────── */}
      {/* Row padding is the single biggest driver of page count on a long
          sale — 24px per row adds up to most of a sheet over 30 items. The
          rule separators carry the row rhythm instead. */}
      <table className="w-full border-collapse mt-4">
        <thead>
          <tr className="bg-black text-white text-[9px] uppercase tracking-wide">
            <th className="py-1.5 pl-3 pr-2 text-left font-normal w-[5%]">#</th>
            <th className="py-1.5 px-2 text-left font-normal w-[45%]">Item &amp; Description</th>
            <th className="py-1.5 px-2 text-right font-normal w-[10%]">Qty</th>
            <th className="py-1.5 px-2 text-right font-normal w-[14%]">Rate</th>
            <th className="py-1.5 px-2 text-right font-normal w-[12%]">Discount</th>
            <th className="py-1.5 pl-2 pr-3 text-right font-normal w-[14%]">Amount</th>
          </tr>
        </thead>
        <tbody>
          {renderRows(partItems, 0)}
          {renderRows(laborItems, partItems.length)}
        </tbody>
      </table>

      {/* ── BANK DETAILS + TOTALS ───────────────────────────────────────── */}
      {/* The totals block only ever fills the right half, so the payment
          details ride in the space beside it rather than costing the invoice
          another band of its own. Kept whole: it looks broken if Sub Total and
          Balance Due — or the bank and its account number — land on different
          sheets. */}
      <div className="flex justify-between items-start gap-6 mt-4 break-inside-avoid">
        <div className="w-[45%]">
          <div className="font-bold text-gray-900 uppercase tracking-wide text-[9px] pb-1 border-b border-gray-300">
            Bank Transfer Details
          </div>
          <table className="w-full border-collapse mt-1.5">
            <tbody>
              {BANK_DETAILS.map(([label, value]) => (
                <tr key={label}>
                  <td className="py-0.5 pr-3 text-gray-600 align-top whitespace-nowrap">{label}</td>
                  {/* The number is what gets copied into a banking app, so it
                      is the one line set solid and spaced out. */}
                  <td
                    className={`py-0.5 align-top ${
                      label === "Account No."
                        ? "font-bold text-gray-900 tracking-wider"
                        : "text-gray-900"
                    }`}
                  >
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[9px] text-gray-500 mt-1.5">
            Please quote invoice # {invoiceId} as the payment reference.
          </div>
        </div>

        <div className="w-[45%]">
          <div className="flex justify-between px-3 py-1.5">
            <span className="text-gray-600">Sub Total</span>
            <span>{formatAmount(grandTotal + totalSavings)}</span>
          </div>
          {totalSavings > 0 && (
            <div className="flex justify-between px-3 py-1.5">
              <span className="text-gray-600">Discount</span>
              <span>- {formatAmount(totalSavings)}</span>
            </div>
          )}
          <div className="flex justify-between px-3 py-1.5">
            <span className="text-gray-600">Total</span>
            <span>{formatAmount(grandTotal)}</span>
          </div>
          {paymentStatus !== "PAID" && (
            <div className="flex justify-between px-3 py-1.5">
              <span className="text-gray-600">Amount Paid</span>
              <span>{formatAmount(amountPaid)}</span>
            </div>
          )}
          <div className="flex justify-between px-3 py-2.5 mt-1 bg-black text-white font-bold">
            <span>Balance Due</span>
            <span>LKR {formatAmount(balanceDue)}</span>
          </div>
        </div>
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      {/* One fine-print run rather than two headed blocks: the old footer spent
          ~33mm on four sentences, which was enough to tip an otherwise
          one-page invoice onto a second sheet. */}
      <div className="mt-5 break-inside-avoid">
        {/* The credit note is a promise specific to this customer ("balance due
            Friday"), so it stays above the rule at body size rather than being
            buried in the fine print. Only unpaid sales pay for this line. */}
        {sale?.credit_note && paymentStatus !== "PAID" && (
          <p className="text-gray-800 mb-2">
            <span className="font-bold text-gray-900">Note: </span>
            {sale.credit_note}
          </p>
        )}
        <p className="text-center text-[9px] leading-snug text-gray-600">
          {footerNotes}
        </p>
      </div>
    </div>
  );
});

export default Invoice;
